import { Plus, X } from "lucide-react";
import {
  CURRENCIES,
  nextAvailableCurrency,
  selectableCurrenciesFor,
  type CurrencyValueRow,
} from "../campaignForm";
import NumberInput from "./NumberInput";

interface CurrencyValueListProps {
  rows: CurrencyValueRow[];
  onChange: (rows: CurrencyValueRow[]) => void;
  valueLabel: string;
  valuePlaceholder?: string;
}

export default function CurrencyValueList({
  rows,
  onChange,
  valueLabel,
  valuePlaceholder,
}: CurrencyValueListProps) {
  const usedCurrencies = rows.map((row) => row.currency);
  const canAddCurrency = rows.length < CURRENCIES.length;

  const addRow = () => {
    onChange([
      ...rows,
      { currency: nextAvailableCurrency(usedCurrencies), value: "" },
    ]);
  };

  const updateRow = (index: number, field: keyof CurrencyValueRow, nextValue: string) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, [field]: nextValue } : row)));
  };

  const removeRow = (index: number) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  return (
    <div>
      {rows.length > 0 && (
        <div className="space-y-[10px]">
          {rows.map((row, index) => (
            <div key={index} className="flex items-end gap-[12px]">
              <div className="w-[140px]">
                <label className="text-[13px] text-[#758089]">Currency</label>
                <select
                  value={row.currency}
                  onChange={(e) => updateRow(index, "currency", e.target.value)}
                  className="mt-[6px] h-[40px] w-full appearance-none rounded-[8px] border border-[#E4E4E7] px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792]"
                >
                  {selectableCurrenciesFor(row.currency, usedCurrencies).map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="text-[13px] text-[#758089]">{valueLabel}</label>
                <NumberInput
                  thousandSeparator
                  min={0}
                  value={row.value}
                  onChange={(e) => updateRow(index, "value", e.target.value)}
                  placeholder={valuePlaceholder}
                  className="mt-[6px] h-[40px] w-full rounded-[8px] border border-[#E4E4E7] px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792]"
                />
              </div>
              <button
                type="button"
                onClick={() => removeRow(index)}
                className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-[8px] border border-[#E4E4E7] text-[#A1A1AA] hover:border-[#DC2626] hover:text-[#DC2626]"
              >
                <X className="size-[14px]" />
              </button>
            </div>
          ))}
        </div>
      )}

      {canAddCurrency && (
        <button
          type="button"
          onClick={addRow}
          className="mt-[12px] inline-flex items-center gap-[6px] text-[13px] font-medium text-[#734792]"
        >
          <Plus className="size-[14px]" />
          Add currency
        </button>
      )}
    </div>
  );
}
